import type { MutableVec2, Vec2 } from "../airport/AirportDefinition";

export const EPSILON = 1e-6;

export function clonePoint(point: Vec2): MutableVec2 {
  return { x: point.x, y: point.y };
}

export function distanceSquared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.sqrt(distanceSquared(a, b));
}

export function angleTo(from: Vec2, to: Vec2): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function smoothStep(value: number, min: number, max: number): number {
  if (max <= min) {
    return value < min ? 0 : 1;
  }
  const t = clamp((value - min) / (max - min), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Signed shortest rotation from one heading to another. */
export function shortestAngleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function signOf(value: number): number {
  return value < 0 ? -1 : 1;
}

export function velocityFor(heading: number, speed: number): MutableVec2 {
  return { x: Math.cos(heading) * speed, y: Math.sin(heading) * speed };
}

export function advancePoint(
  point: Vec2,
  velocity: Vec2,
  seconds: number,
): MutableVec2 {
  return {
    x: point.x + velocity.x * seconds,
    y: point.y + velocity.y * seconds,
  };
}

export function pointToSegmentDistance(
  point: Vec2,
  start: Vec2,
  end: Vec2,
): number {
  const lengthSquared = distanceSquared(start, end);
  if (lengthSquared <= EPSILON) {
    return distance(point, start);
  }
  const t = clamp(
    ((point.x - start.x) * (end.x - start.x) +
      (point.y - start.y) * (end.y - start.y)) /
      lengthSquared,
    0,
    1,
  );
  return distance(point, {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t,
  });
}

/** Total length of a polyline that starts at `from`. */
export function pathLength(from: Vec2, points: readonly Vec2[]): number {
  let total = 0;
  let previous = from;
  for (const point of points) {
    total += distance(previous, point);
    previous = point;
  }
  return total;
}

/**
 * Whether a point lies inside a convex ring, for either winding order.
 *
 * A point exactly on an edge counts as inside.
 */
export function isInsideConvexPolygon(
  point: Vec2,
  corners: readonly Vec2[],
): boolean {
  if (corners.length < 3) {
    return false;
  }

  let sawPositive = false;
  let sawNegative = false;
  for (let i = 0; i < corners.length; i++) {
    const from = corners[i]!;
    const to = corners[(i + 1) % corners.length]!;
    const cross =
      (to.x - from.x) * (point.y - from.y) -
      (to.y - from.y) * (point.x - from.x);
    if (cross > EPSILON) {
      sawPositive = true;
    } else if (cross < -EPSILON) {
      sawNegative = true;
    }
    if (sawPositive && sawNegative) {
      return false;
    }
  }
  return true;
}

/** Shortest distance from a point to a ring's boundary; 0 for an empty ring. */
export function distanceToPolygonEdge(
  point: Vec2,
  corners: readonly Vec2[],
): number {
  if (corners.length < 2) {
    return 0;
  }

  let closest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < corners.length; i++) {
    const from = corners[i]!;
    const to = corners[(i + 1) % corners.length]!;
    closest = Math.min(closest, pointToSegmentDistance(point, from, to));
  }
  return closest;
}

/** Fraction of the runway axis covered by `point`, clamped to [0, 1]. */
export function axisProgress(point: Vec2, from: Vec2, to: Vec2): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy || 1;
  return clamp(
    ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared,
    0,
    1,
  );
}
